import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { History, HelpCircle, X, GripVertical, Trash2, Plus, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { toCents, money } from '../money';

// Online full-screen form frame: header (history, name+number, help, X) + scroll body + sticky footer.
export function FormFrame({ name, number, dirty, supportsRecurring = false, onRecurring,
  onCancel, onSave, saving, error, canSave = true, footerLeftExtra, children }) {
  const [splitOpen, setSplitOpen] = useState(false);
  const close = () => {
    if (dirty && !window.confirm('Leave without saving? Your changes will be lost.')) return;
    onCancel();
  };
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-100" data-testid="online-form-frame">
      <header className="flex items-center gap-3 h-14 px-4 bg-emerald-900 text-white shrink-0">
        <History className="h-5 w-5 opacity-80" data-testid="form-history" />
        <h2 className="text-lg font-semibold flex-1 truncate" data-testid="form-title">{name}{number ? ` ${number}` : ''}</h2>
        <HelpCircle className="h-5 w-5 opacity-80" data-testid="form-help" />
        <button onClick={close} data-testid="form-close" className="p-1 rounded hover:bg-white/10"><X className="h-5 w-5" /></button>
      </header>
      <div className="flex-1 overflow-auto p-5" data-testid="form-body">
        <div className="mx-auto max-w-5xl space-y-5">
          {error && <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-400" data-testid="form-error">{error}</div>}
          {children}
        </div>
      </div>
      <footer className="flex items-center gap-2 h-16 px-4 border-t border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 shrink-0" data-testid="form-footer">
        <Button variant="ghost" onClick={close} data-testid="form-cancel">Cancel</Button>
        {footerLeftExtra}
        <div className="flex-1" />
        <div className="relative flex">
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-r-none" disabled={!canSave || saving} onClick={() => onSave('new')} data-testid="form-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <button className="bg-emerald-700 hover:bg-emerald-800 text-white px-2 rounded-r-md border-l border-emerald-500" disabled={!canSave || saving}
            onClick={() => setSplitOpen((v) => !v)} data-testid="form-save-split"><ChevronDown className="h-4 w-4" /></button>
          {splitOpen && (
            <div className="absolute bottom-11 right-0 w-44 rounded-lg border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-xl" data-testid="form-save-menu">
              <button className="block w-full text-left px-3 py-2 text-sm hover:bg-emerald-600/10" onClick={() => { setSplitOpen(false); onSave('close'); }} data-testid="form-save-close">Save and close</button>
              <button className="block w-full text-left px-3 py-2 text-sm hover:bg-emerald-600/10" onClick={() => { setSplitOpen(false); onSave('new'); }} data-testid="form-save-new">Save and new</button>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

export const Field = ({ label, children, className = '' }) => (
  <label className={`space-y-1 block ${className}`}>
    <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
    {children}
  </label>
);

// OL Finance searchable combobox with optional "+ Add new" quick-add.
export function Combo({ value, onChange, options = [], placeholder, testid, onAdd }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const sel = options.find((o) => o.id === value);
  const filt = options.filter((o) => (o.label || '').toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="relative" data-testid={testid}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="h-9 w-full rounded border border-slate-300 dark:border-slate-600 bg-transparent px-2 text-sm text-left flex items-center justify-between text-slate-800 dark:text-slate-100">
        <span className={sel ? '' : 'text-slate-400'}>{sel ? sel.label : (placeholder || 'Select')}</span>
        <ChevronsUpDown className="h-4 w-4 text-slate-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type to filter"
            className="w-full h-8 px-2 text-sm border-b border-slate-200 dark:border-slate-700 bg-transparent text-slate-800 dark:text-slate-100" data-testid={`${testid}-search`} />
          <div className="max-h-56 overflow-auto">
            {filt.map((o) => (
              <button key={o.id} type="button" onClick={() => { onChange(o.id); setOpen(false); setQ(''); }}
                className="block w-full text-left px-2 py-1.5 text-sm hover:bg-emerald-600/10 text-slate-800 dark:text-slate-100">{o.label}</button>
            ))}
            {filt.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">No matches</div>}
          </div>
          {onAdd && (
            <button type="button" data-testid={`${testid}-add`}
              onClick={async () => { const name = window.prompt('Add new'); if (name && name.trim()) { const id = await onAdd(name.trim()); if (id) onChange(id); } setOpen(false); setQ(''); }}
              className="block w-full text-left px-2 py-1.5 text-sm text-emerald-600 border-t border-slate-200 dark:border-slate-700">+ Add new</button>
          )}
        </div>
      )}
    </div>
  );
}

// Reusable Online line grid: drag handle, #, cells, delete; Add lines / Clear all lines; totals bottom-right.
export function LineGrid({ cols, lines, setLines, blank, moneyKey = 'amount', isDark }) {
  const [drag, setDrag] = useState(null);
  const head = isDark ? 'text-slate-400' : 'text-slate-500';
  const setLine = (i, k, v) => setLines(lines.map((l, j) => {
    if (j !== i) return l;
    const nl = { ...l, [k]: v };
    if ((k === 'qty' || k === 'rate') && nl.qty && nl.rate) nl.amount = (parseFloat(nl.qty) * parseFloat(nl.rate)).toFixed(2);
    if ((k === 'qty' || k === 'cost') && nl.qty && nl.cost) nl.amount = (parseFloat(nl.qty) * parseFloat(nl.cost)).toFixed(2);
    return nl;
  }));
  const drop = (i) => { if (drag == null || drag === i) return; const a = [...lines]; const [m] = a.splice(drag, 1); a.splice(i, 0, m); setLines(a); setDrag(null); };
  const total = lines.reduce((s, l) => s + toCents(l[moneyKey]), 0);
  const gridCols = `28px 28px ${cols.map((c) => c.w || '1fr').join(' ')} 28px`;
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="line-grid">
      <div className={`grid gap-2 px-2 py-1.5 text-xs ${head} bg-slate-50 dark:bg-slate-800/60`} style={{ gridTemplateColumns: gridCols }}>
        <span /><span>#</span>{cols.map((c) => <span key={c.key} className={c.money ? 'text-right' : ''}>{c.label}</span>)}<span />
      </div>
      {lines.map((l, i) => (
        <div key={i} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(i)}
          className="grid gap-2 px-2 py-1 items-center border-t border-slate-100 dark:border-slate-800" style={{ gridTemplateColumns: gridCols }} data-testid={`line-row-${i}`}>
          <span className="cursor-grab text-slate-400" data-testid={`line-drag-${i}`}><GripVertical className="h-4 w-4" /></span>
          <span className="text-xs text-slate-400">{i + 1}</span>
          {cols.map((c) => {
            if (c.kind === 'account' || c.kind === 'item') {
              return (
                <Combo key={c.key} value={l[c.key] || ''} onChange={(v) => setLine(i, c.key, v)}
                  options={c.options || []} placeholder={c.placeholder || 'Select'} testid={`line-${c.key}-${i}`} />
              );
            }
            return (
              <input key={c.key} value={l[c.key] || ''} onChange={(e) => setLine(i, c.key, e.target.value)}
                placeholder={c.placeholder || ''} data-testid={`line-${c.key}-${i}`}
                className={`h-8 rounded border border-slate-300 dark:border-slate-600 bg-transparent px-2 text-sm ${c.money ? 'text-right tabular-nums' : ''}`} />
            );
          })}
          <button onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`line-del-${i}`} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex items-center justify-between px-2 py-2 border-t border-slate-200 dark:border-slate-700">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setLines([...lines, { ...blank }])} data-testid="line-add"><Plus className="h-4 w-4 mr-1" />Add lines</Button>
          <Button size="sm" variant="ghost" onClick={() => setLines([{ ...blank }])} data-testid="line-clear">Clear all lines</Button>
        </div>
        <div className="text-sm font-semibold tabular-nums" data-testid="line-total">Total {money(total)}</div>
      </div>
    </div>
  );
}
